/**
 * Academy Video Player Component
 * Sora 2 AI-generated course intro/outro videos with progress tracking
 */

import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  Play, Pause, Volume2, VolumeX, Maximize, RefreshCw,
  Video, Film, Sparkles, Loader2, AlertCircle, CheckCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Video Player Component
const VideoPlayer = ({ videoUrl, title, onComplete, autoPlay = false }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);

  useEffect(() => {
    if (autoPlay && videoRef.current) {
      videoRef.current.play().catch(() => {
        // Autoplay blocked, need user interaction
      });
    }
  }, [autoPlay]);

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const current = videoRef.current.currentTime;
      const dur = videoRef.current.duration;
      setCurrentTime(current);
      setDuration(dur);
      setProgress((current / dur) * 100);
      
      // Mark as completed when 90% watched
      if (!isCompleted && current / dur >= 0.9) {
        setIsCompleted(true);
        onComplete?.();
      }
    }
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = x / rect.width;
    if (videoRef.current) {
      videoRef.current.currentTime = percent * duration;
    }
  };

  const handleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      }
    }
  };

  const formatTime = (time) => {
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="relative bg-black group" data-testid="video-player">
      <video
        ref={videoRef}
        src={videoUrl}
        className="w-full aspect-video"
        onTimeUpdate={handleTimeUpdate}
        onEnded={() => setIsPlaying(false)}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        muted={isMuted}
        playsInline
      />
      
      {/* Overlay Controls */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between">
        {/* Title */}
        <div className="p-4">
          <div className="flex items-center gap-2">
            <Film className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-white text-sm font-medium">{title}</span>
            {isCompleted && (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-green-500/20 text-green-400 text-xs">
                <CheckCircle className="w-3 h-3" />
                Watched
              </span>
            )}
          </div>
        </div>
        
        {/* Center Play Button */}
        <div className="flex-1 flex items-center justify-center">
          <button
            onClick={handlePlayPause}
            className="w-16 h-16 flex items-center justify-center bg-[#D4AF37]/80 hover:bg-[#D4AF37] rounded-full transition-colors"
          >
            {isPlaying ? (
              <Pause className="w-8 h-8 text-black" />
            ) : (
              <Play className="w-8 h-8 text-black ml-1" />
            )}
          </button>
        </div>
        
        {/* Bottom Controls */}
        <div className="p-4 space-y-2">
          {/* Progress Bar */}
          <div 
            className="h-1 bg-white/20 cursor-pointer relative"
            onClick={handleSeek}
          >
            <div 
              className="h-full bg-[#D4AF37] relative"
              style={{ width: `${progress}%` }}
            >
              <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-[#D4AF37] rounded-full -mr-1.5" />
            </div>
          </div>
          
          {/* Controls Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={handlePlayPause} className="text-white hover:text-[#D4AF37]">
                {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
              </button>
              <button onClick={() => setIsMuted(!isMuted)} className="text-white hover:text-[#D4AF37]">
                {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>
              <span className="text-white text-sm">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
            </div>
            <button onClick={handleFullscreen} className="text-white hover:text-[#D4AF37]">
              <Maximize className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Video Generation Status Card
const VideoGenerationCard = ({ courseId, videoType, onGenerated }) => {
  const [status, setStatus] = useState('idle'); // idle, generating, completed, failed
  const [videoId, setVideoId] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [selectedDuration, setSelectedDuration] = useState(8); // Default to 8 seconds

  const DURATION_OPTIONS = [
    { value: 4, label: '4 sec', description: 'Quick intro' },
    { value: 8, label: '8 sec', description: 'Standard' },
    { value: 12, label: '12 sec', description: 'Extended' }
  ];

  const startGeneration = async () => {
    setStatus('generating');
    setError(null);
    setProgress(0);
    
    try {
      const response = await axios.post(`${API}/academy/videos/generate`, {
        course_id: courseId,
        video_type: videoType,
        duration: selectedDuration,
        size: "1280x720"
      });
      
      if (response.data.success) {
        setVideoId(response.data.video_id);
        pollStatus(response.data.video_id);
      }
    } catch (err) {
      setStatus('failed');
      setError(err.response?.data?.detail || 'Failed to start video generation');
    }
  };

  const pollStatus = async (id) => {
    const checkStatus = async () => {
      try {
        const response = await axios.get(`${API}/academy/videos/status/${id}`);
        const data = response.data;
        
        if (data.status === 'completed') {
          setStatus('completed');
          setVideoUrl(data.url);
          onGenerated?.(data);
        } else if (data.status === 'failed') {
          setStatus('failed');
          setError(data.error || 'Video generation failed');
        } else {
          // Still generating - increment fake progress
          setProgress(prev => Math.min(prev + 5, 95));
          setTimeout(checkStatus, 10000); // Check every 10 seconds
        }
      } catch (err) {
        setStatus('failed');
        setError('Failed to check video status');
      }
    };
    
    checkStatus();
  };

  return (
    <div className="bg-[#0A0A0A] border border-white/10 p-6" data-testid="video-generation-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-[#D4AF37]/20">
          <Sparkles className="w-6 h-6 text-[#D4AF37]" />
        </div>
        <div>
          <h3 className="text-white font-bold">AI Video {videoType === 'intro' ? 'Intro' : 'Outro'}</h3>
          <p className="text-white/50 text-sm">Generate with Sora 2 AI</p>
        </div>
      </div>
      
      {status === 'idle' && (
        <div className="text-center py-6">
          <Video className="w-12 h-12 text-white/20 mx-auto mb-4" />
          <p className="text-white/60 text-sm mb-4">
            Generate a custom AI video {videoType} for this course using Sora 2.
          </p>
          
          {/* Duration Selector */}
          <div className="mb-6">
            <p className="text-white/40 text-xs uppercase tracking-wider mb-3">Video Duration</p>
            <div className="flex justify-center gap-2">
              {DURATION_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setSelectedDuration(option.value)}
                  className={`px-4 py-2 border transition-all ${
                    selectedDuration === option.value
                      ? 'border-[#D4AF37] bg-[#D4AF37]/20 text-white'
                      : 'border-white/10 text-white/50 hover:border-white/30'
                  }`}
                >
                  <span className="font-bold">{option.label}</span>
                  <span className="text-xs block text-white/40">{option.description}</span>
                </button>
              ))}
            </div>
          </div>
          
          <button
            onClick={startGeneration}
            className="px-6 py-3 bg-[#D4AF37] text-black font-bold hover:bg-[#C4A030] transition-colors flex items-center gap-2 mx-auto"
          >
            <Sparkles className="w-5 h-5" />
            Generate {selectedDuration}s Video
          </button>
          <p className="text-white/30 text-xs mt-3">
            Generation takes 2-5 minutes depending on duration
          </p>
        </div>
      )}
      
      {status === 'generating' && (
        <div className="text-center py-8">
          <Loader2 className="w-16 h-16 text-[#D4AF37] animate-spin mx-auto mb-4" />
          <p className="text-white font-medium mb-2">Generating Video...</p>
          <p className="text-white/50 text-sm mb-4">
            Sora 2 is creating your video. This may take 2-5 minutes.
          </p>
          <div className="w-full max-w-xs mx-auto bg-white/10 h-2 rounded-full overflow-hidden">
            <div 
              className="h-full bg-[#D4AF37] transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-white/40 text-xs mt-2">~{Math.max(1, Math.round((100 - progress) / 5))} min remaining</p>
        </div>
      )}
      
      {status === 'completed' && videoUrl && (
        <div>
          <VideoPlayer 
            videoUrl={`${API.replace('/api', '')}${videoUrl}`}
            title={`${courseId} ${videoType}`}
          />
          <div className="flex items-center justify-between mt-4">
            <div className="flex items-center gap-2 text-green-400 text-sm">
              <CheckCircle className="w-4 h-4" />
              Video Ready
            </div>
            <button
              onClick={startGeneration}
              className="text-white/50 hover:text-white text-sm flex items-center gap-1"
            >
              <RefreshCw className="w-4 h-4" />
              Regenerate
            </button>
          </div>
        </div>
      )}
      
      {status === 'failed' && (
        <div className="text-center py-8">
          <AlertCircle className="w-16 h-16 text-red-400/50 mx-auto mb-4" />
          <p className="text-red-400 font-medium mb-2">Generation Failed</p>
          <p className="text-white/50 text-sm mb-4">{error}</p>
          <button
            onClick={startGeneration}
            className="px-6 py-3 bg-white/10 text-white hover:bg-white/20 transition-colors flex items-center gap-2 mx-auto"
          >
            <RefreshCw className="w-5 h-5" />
            Try Again
          </button>
        </div>
      )}
    </div>
  );
};

// Course Video Section (shows available videos for a course)
const CourseVideoSection = ({ courseId, userId }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeVideo, setActiveVideo] = useState(null);
  const [watchedVideos, setWatchedVideos] = useState(new Set());

  useEffect(() => {
    fetchCourseVideos();
  }, [courseId]);

  const fetchCourseVideos = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/academy/videos/course/${courseId}`);
      setVideos(response.data.videos || []);
    } catch (err) {
      console.error('Failed to fetch videos:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleVideoComplete = (videoId) => {
    setWatchedVideos(prev => new Set([...prev, videoId]));
    // Could also save progress to backend here
  };

  const completedVideos = videos.filter(v => v.status === 'completed');
  const hasIntro = completedVideos.some(v => v.video_type === 'intro');
  const hasOutro = completedVideos.some(v => v.video_type === 'outro');

  return (
    <div className="space-y-6" data-testid="course-video-section">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-500/20">
            <Film className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h3 className="text-white font-bold text-lg">Course Videos</h3>
            <p className="text-white/50 text-sm">AI-generated intros and content</p>
          </div>
        </div>
        <div className="text-sm text-white/40">
          {watchedVideos.size}/{completedVideos.length} watched
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {/* Course Intro Video */}
          {hasIntro ? (
            completedVideos.filter(v => v.video_type === 'intro').map(video => (
              <div key={video.video_id} className="bg-[#0A0A0A] border border-white/10 overflow-hidden">
                <VideoPlayer
                  videoUrl={`${API.replace('/api', '')}${video.url}`}
                  title="Course Intro"
                  onComplete={() => handleVideoComplete(video.video_id)}
                />
                <div className="p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-white text-sm font-medium">Course Introduction</span>
                    {watchedVideos.has(video.video_id) && (
                      <span className="text-green-400 text-xs flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" /> Watched
                      </span>
                    )}
                  </div>
                  <span className="text-white/40 text-xs">{video.duration}s • AI Generated</span>
                </div>
              </div>
            ))
          ) : (
            <VideoGenerationCard 
              courseId={courseId}
              videoType="intro"
              onGenerated={fetchCourseVideos}
            />
          )}

          {/* Course Outro Video */}
          {hasOutro ? (
            completedVideos.filter(v => v.video_type === 'outro').map(video => (
              <div key={video.video_id} className="bg-[#0A0A0A] border border-white/10 overflow-hidden">
                <VideoPlayer
                  videoUrl={`${API.replace('/api', '')}${video.url}`}
                  title="Course Outro"
                  onComplete={() => handleVideoComplete(video.video_id)}
                />
                <div className="p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-white text-sm font-medium">Course Completion</span>
                    {watchedVideos.has(video.video_id) && (
                      <span className="text-green-400 text-xs flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" /> Watched
                      </span>
                    )}
                  </div>
                  <span className="text-white/40 text-xs">{video.duration}s • AI Generated</span>
                </div>
              </div>
            ))
          ) : (
            <VideoGenerationCard 
              courseId={courseId}
              videoType="outro"
              onGenerated={fetchCourseVideos}
            />
          )}
        </div>
      )}

      {/* Info Banner */}
      <div className="p-4 bg-purple-500/10 border border-purple-500/30 flex items-start gap-3">
        <Sparkles className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-white text-sm font-medium">Powered by Sora 2 AI</p>
          <p className="text-white/60 text-xs">
            Course videos are generated using OpenAI's Sora 2 video model.
            Each video takes 2-5 minutes to generate. Videos are unique to your course experience.
          </p>
        </div>
      </div>
    </div>
  );
};

export { VideoPlayer, VideoGenerationCard, CourseVideoSection };
export default CourseVideoSection;
